import { useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays, addHours } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, ImagePlus, Gavel, ShoppingCart, CalendarIcon, Clock, Upload, Loader2, X, Recycle } from "lucide-react";
import { ProductImageGallery } from "../components/ProductImageGallery";
import { ImageLibraryPicker } from "../components/ImageLibraryPicker";
import { CATEGORIES, normalizeCategory } from "../lib/categories";

const SAMPLE_IMAGES = [
  "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1507582020474-9a35b7d450d7?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800&q=80"
];

const CONDITION_OPTIONS = ["Nuevo", "Usado - Excelente", "Usado - Bueno", "Para piezas"];

const CreateProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);
  
  // Check for republish data
  const republishProduct = location.state?.republishProduct;
  
  const [title, setTitle] = useState(republishProduct?.title || "");
  const [description, setDescription] = useState(republishProduct?.description || "");
  const [imageUrl, setImageUrl] = useState(republishProduct?.image_url || "");
  const [additionalImages, setAdditionalImages] = useState(republishProduct?.images || []);
  const [saleType, setSaleType] = useState(republishProduct?.sale_type || "direct");
  const [category, setCategory] = useState(normalizeCategory(republishProduct?.category));
  const [price, setPrice] = useState(republishProduct?.price?.toString() || "");
  const [buyNowPrice, setBuyNowPrice] = useState(republishProduct?.buy_now_price?.toString() || "");
  const [auctionEndDate, setAuctionEndDate] = useState(null);
  const [auctionEndTime, setAuctionEndTime] = useState("12:00");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  
  // Discount coupon states
  const [discountCode, setDiscountCode] = useState(republishProduct?.discount_code || "");
  const [discountPercent, setDiscountPercent] = useState(republishProduct?.discount_percent?.toString() || "");

  // Offer negotiation settings (direct sale only)
  const [acceptsOffers, setAcceptsOffers] = useState(
    republishProduct?.accepts_offers !== undefined ? republishProduct.accepts_offers : true
  );
  const [autoAcceptOfferPrice, setAutoAcceptOfferPrice] = useState(
    republishProduct?.auto_accept_offer_price?.toString() || ""
  );

  // Ficha técnica (optional)
  const [conditionGrade, setConditionGrade] = useState(republishProduct?.condition_grade || "");
  const [storageRam, setStorageRam] = useState(republishProduct?.storage_ram || "");
  const [includedAccessories, setIncludedAccessories] = useState(republishProduct?.included_accessories || "");
  const [warrantyInfo, setWarrantyInfo] = useState(republishProduct?.warranty_info || "");
  
  // Scheduled publication states
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("09:00");

  const handleFileUpload = async (e, isAdditional = false) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`"${file.name}": Solo JPG, PNG, WebP o GIF`);
        e.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}": no puede superar 5MB`);
        e.target.value = "";
        return;
      }
    }

    // Routing: main button → first becomes main, rest go to additional
    //         additional button → all go to additional
    const mainFile = isAdditional ? null : files[0];
    const additionalFiles = isAdditional ? files : files.slice(1);

    if (additionalImages.length + additionalFiles.length > 9) {
      const available = Math.max(0, 9 - additionalImages.length);
      toast.error(`Máximo 10 imágenes. Puedes agregar hasta ${available} adicionales.`);
      e.target.value = "";
      return;
    }

    setUploading(true);
    let successCount = 0;
    try {
      if (mainFile) {
        const formData = new FormData();
        formData.append("file", mainFile);
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          withCredentials: true
        });
        setImageUrl(response.data.url);
        setShowImagePicker(false);
        successCount++;
      }
      for (const file of additionalFiles) {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          withCredentials: true
        });
        setAdditionalImages(prev => [...prev, response.data.url]);
        successCount++;
      }
      toast.success(`${successCount} ${successCount === 1 ? "imagen subida" : "imágenes subidas"} exitosamente`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  
  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRecycleSelect = (urls) => {
    let main = imageUrl;
    const extra = [...additionalImages];
    let added = 0;
    for (const url of urls) {
      if (url === main || extra.includes(url)) continue;
      if (!main) { main = url; added++; }
      else if (extra.length < 9) { extra.push(url); added++; }
    }
    setImageUrl(main);
    setAdditionalImages(extra);
    setShowLibrary(false);
    if (added > 0) toast.success(`${added} foto${added !== 1 ? "s" : ""} reciclada${added !== 1 ? "s" : ""} de tu galería`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !description || !imageUrl || !price) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    if (saleType === "auction" && !auctionEndDate) {
      toast.error("Por favor selecciona la fecha de fin de subasta");
      return;
    }

    if (isScheduled && !scheduledDate) {
      toast.error("Por favor selecciona la fecha de publicación programada");
      return;
    }

    setLoading(true);
    try {
      let auctionEnd = null;
      if (saleType === "auction" && auctionEndDate) {
        const [hours, minutes] = auctionEndTime.split(":").map(Number);
        auctionEnd = new Date(auctionEndDate);
        auctionEnd.setHours(hours, minutes, 0, 0);
      }

      let scheduledPublishDate = null;
      if (isScheduled && scheduledDate) {
        const [hours, minutes] = scheduledTime.split(":").map(Number);
        scheduledPublishDate = new Date(scheduledDate);
        scheduledPublishDate.setHours(hours, minutes, 0, 0);
      }

      const productData = {
        title,
        description,
        image_url: imageUrl,
        images: additionalImages,
        sale_type: saleType,
        price: parseFloat(price),
        buy_now_price: buyNowPrice ? parseFloat(buyNowPrice) : parseFloat(price),
        auction_end_time: auctionEnd?.toISOString() || null,
        scheduled_publish_date: scheduledPublishDate?.toISOString() || null,
        category: category,
        discount_code: discountCode || null,
        discount_percent: discountPercent ? parseFloat(discountPercent) : null,
        accepts_offers: saleType === "direct" ? acceptsOffers : false,
        auto_accept_offer_price: (saleType === "direct" && acceptsOffers && autoAcceptOfferPrice)
          ? parseFloat(autoAcceptOfferPrice)
          : null,
        condition_grade: conditionGrade || null,
        storage_ram: storageRam || null,
        included_accessories: includedAccessories || null,
        warranty_info: warrantyInfo || null
      };

      await axios.post(`${API}/products`, productData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success(isScheduled ? "¡Producto programado exitosamente!" : "¡Producto publicado exitosamente!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al publicar producto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-lg mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        <h1 className="text-2xl font-bold text-[#0F172A] mb-6">Publicar Producto</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image */}
          <div className="space-y-2">
            <Label>Imagen del producto</Label>
            {imageUrl ? (
              <div className="relative rounded-2xl overflow-hidden">
                <img src={imageUrl} alt="Preview" className="w-full aspect-square object-cover" />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-4 right-4 rounded-full"
                  onClick={() => setShowImagePicker(true)}
                >
                  Cambiar
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowImagePicker(true)}
                className="w-full aspect-square rounded-2xl border-2 border-dashed border-[#E2E8F0] bg-white flex flex-col items-center justify-center gap-3 hover:border-[#475569] transition-colors"
                data-testid="add-image"
              >
                <ImagePlus size={48} className="text-[#64748B]" />
                <span className="text-[#64748B]">Agregar imagen</span>
              </button>
            )}

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
            />

            {/* Image picker modal */}
            {showImagePicker && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowImagePicker(false)}>
                <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-slide-up" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-semibold text-[#0F172A] mb-1">Seleccionar imagen(es)</h3>
                  <p className="text-xs text-[#64748B] mb-4">Puedes seleccionar varias — la primera será la imagen principal y el resto se agregarán como adicionales.</p>
                  
                  <div className="space-y-4">
                    {/* Upload from device button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-14 rounded-xl border-2 border-dashed border-[#475569] text-[#475569] hover:bg-[#475569]/5"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-from-device"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-5 w-5" />
                          Subir desde dispositivo
                        </>
                      )}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-[#E2E8F0]" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-[#64748B]">O usa una URL</span>
                      </div>
                    </div>

                    <Input
                      placeholder="URL de la imagen"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      className="rounded-xl"
                      data-testid="image-url-input"
                    />
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-[#E2E8F0]" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-[#64748B]">O selecciona una de muestra</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      {SAMPLE_IMAGES.map((url, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setImageUrl(url);
                            setShowImagePicker(false);
                          }}
                          className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            imageUrl === url ? "border-[#475569]" : "border-transparent"
                          }`}
                        >
                          <img src={url} alt={`Sample ${index + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => setShowImagePicker(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      className="flex-1 rounded-full bg-[#475569] hover:bg-[#334155]"
                      onClick={() => setShowImagePicker(false)}
                      disabled={!imageUrl}
                    >
                      Confirmar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Unified gallery (main + additional) with drag&drop reordering */}
          <div className="space-y-2">
            <Label>Galería de imágenes ({(imageUrl ? 1 : 0) + additionalImages.length}/10)</Label>
            <input
              ref={additionalFileInputRef}
              type="file"
              onChange={(e) => handleFileUpload(e, true)}
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
            />

            {imageUrl && (
              <ProductImageGallery
                images={[imageUrl, ...additionalImages]}
                onReorder={(next) => {
                  setImageUrl(next[0] || "");
                  setAdditionalImages(next.slice(1));
                }}
                onRemove={(idx) => {
                  const all = [imageUrl, ...additionalImages];
                  all.splice(idx, 1);
                  setImageUrl(all[0] || "");
                  setAdditionalImages(all.slice(1));
                }}
              />
            )}

            {imageUrl && additionalImages.length < 9 && (
              <button
                type="button"
                onClick={() => additionalFileInputRef.current?.click()}
                disabled={uploading}
                className="w-full py-2.5 rounded-lg border-2 border-dashed border-[#CBD5E1] flex items-center justify-center gap-2 text-[#64748B] hover:border-[#475569] hover:text-[#475569] transition-colors text-sm"
              >
                {uploading ? (
                  <><Loader2 className="animate-spin" size={16} /> Subiendo...</>
                ) : (
                  <><ImagePlus size={16} /> Agregar más imágenes</>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowLibrary(true)}
              className="w-full py-2.5 rounded-lg border-2 border-dashed border-emerald-300 flex items-center justify-center gap-2 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-sm"
              data-testid="recycle-images-btn"
            >
              <Recycle size={16} /> Reciclar fotos anteriores
            </button>
            <ImageLibraryPicker
              open={showLibrary}
              onOpenChange={setShowLibrary}
              onSelect={handleRecycleSelect}
              maxSelectable={10 - ((imageUrl ? 1 : 0) + additionalImages.length)}
              excludeUrls={[imageUrl, ...additionalImages].filter(Boolean)}
            />
            <p className="text-xs text-[#64748B]">Máximo 10 fotos en total (1 principal + 9 adicionales)</p>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: MacBook Pro 2023"
              className="rounded-xl border-[#E2E8F0]"
              data-testid="title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe tu producto..."
              className="rounded-xl border-[#E2E8F0] min-h-[100px]"
              data-testid="description-input"
            />
          </div>

          {/* Ficha técnica (optional) */}
          <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <Label className="text-[#0F172A] font-semibold">Ficha técnica (opcional)</Label>
            <div className="space-y-2">
              <Label className="text-sm">Condición / Grado</Label>
              <div className="flex flex-wrap gap-2" data-testid="condition-grade-selector">
                {CONDITION_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setConditionGrade(conditionGrade === opt ? "" : opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      conditionGrade === opt
                        ? "bg-[#475569] text-white border-[#475569]"
                        : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#475569]"
                    }`}
                    data-testid={`condition-grade-${opt.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="storageRam" className="text-sm">Almacenamiento y RAM</Label>
              <Input
                id="storageRam"
                value={storageRam}
                onChange={(e) => setStorageRam(e.target.value)}
                placeholder="Ej: 256GB / 8GB RAM"
                className="rounded-xl border-[#E2E8F0]"
                data-testid="storage-ram-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="includedAccessories" className="text-sm">Accesorios incluidos</Label>
              <Input
                id="includedAccessories"
                value={includedAccessories}
                onChange={(e) => setIncludedAccessories(e.target.value)}
                placeholder="Ej: Cargador, caja original"
                className="rounded-xl border-[#E2E8F0]"
                data-testid="included-accessories-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="warrantyInfo" className="text-sm">Garantía</Label>
              <Input
                id="warrantyInfo"
                value={warrantyInfo}
                onChange={(e) => setWarrantyInfo(e.target.value)}
                placeholder="Ej: 30 días de garantía Clueer"
                className="rounded-xl border-[#E2E8F0]"
                data-testid="warranty-info-input"
              />
            </div>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Categoría</Label>
            <div className="flex flex-wrap gap-2" data-testid="category-selector">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    category === cat.id
                      ? "bg-[#475569] text-white border-[#475569]"
                      : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#475569]"
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sale Type */}
          <div className="space-y-3">
            <Label>Tipo de venta</Label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSaleType("direct")}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  saleType === "direct"
                    ? "border-[#475569] bg-[#475569]/5"
                    : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                }`}
                data-testid="sale-type-direct"
              >
                <ShoppingCart size={24} className={saleType === "direct" ? "text-[#475569]" : "text-[#64748B]"} />
                <div className="text-left">
                  <p className="font-semibold text-[#0F172A]">Venta Directa</p>
                  <p className="text-xs text-[#64748B]">Precio fijo</p>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setSaleType("auction")}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  saleType === "auction"
                    ? "border-[#475569] bg-[#475569]/5"
                    : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                }`}
                data-testid="sale-type-auction"
              >
                <Gavel size={24} className={saleType === "auction" ? "text-[#475569]" : "text-[#64748B]"} />
                <div className="text-left">
                  <p className="font-semibold text-[#0F172A]">Subasta</p>
                  <p className="text-xs text-[#64748B]">Precio inicial</p>
                </div>
              </button>
            </div>
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label htmlFor="price">{saleType === "auction" ? "Precio inicial" : "Precio"}</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
              <Input
                id="price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                className="pl-8 rounded-xl border-[#E2E8F0]"
                data-testid="price-input"
              />
            </div>
          </div>

          {/* Buy Now Price */}
          <div className="space-y-2">
            <Label htmlFor="buyNowPrice">
              Precio "Comprar Ahora"
              <span className="text-xs text-[#64748B] ml-2">(opcional)</span>
            </Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
              <Input
                id="buyNowPrice"
                type="number"
                value={buyNowPrice}
                onChange={(e) => setBuyNowPrice(e.target.value)}
                placeholder={price || "0.00"}
                className="pl-8 rounded-xl border-[#E2E8F0]"
                data-testid="buy-now-price-input"
              />
            </div>
            <p className="text-xs text-[#64748B]">
              Si no lo defines, se usará el precio {saleType === "auction" ? "inicial" : "base"}
            </p>
          </div>
          
          {/* Offers / Fixed Price toggle - only for direct sales */}
          {saleType === "direct" && (
            <div className="space-y-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label className="text-emerald-900 font-semibold">¿Aceptar ofertas?</Label>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Si desactivás, el producto se vende solo al precio fijo (no aparece "Último precio?").
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={acceptsOffers}
                  onClick={() => setAcceptsOffers(!acceptsOffers)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${acceptsOffers ? 'bg-emerald-600' : 'bg-gray-300'}`}
                  data-testid="accepts-offers-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${acceptsOffers ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {acceptsOffers && (
                <div className="space-y-2 pt-2 border-t border-emerald-200">
                  <Label className="text-emerald-900 text-sm">Precio mínimo de auto-aceptación (opcional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-semibold">$</span>
                    <Input
                      type="number"
                      placeholder="Ej: 180"
                      value={autoAcceptOfferPrice}
                      onChange={(e) => setAutoAcceptOfferPrice(e.target.value)}
                      className="pl-7 rounded-xl border-emerald-200"
                      min="0"
                      step="0.01"
                      data-testid="auto-accept-price-input"
                    />
                  </div>
                  <p className="text-xs text-emerald-700">
                    Toda oferta ≥ este monto será aceptada automáticamente sin tu intervención. Dejalo vacío para revisar todas las ofertas manualmente.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Discount Coupon - Only for direct sales */}
          {saleType === "direct" && (
            <div className="space-y-3 p-4 bg-purple-50 border border-purple-200 rounded-xl">
              <Label className="text-purple-800">Cupón de descuento (opcional)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Input
                    placeholder="Código (ej: DESC20)"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    className="rounded-xl border-purple-200"
                    data-testid="discount-code-input"
                  />
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    placeholder="% descuento"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    className="pr-8 rounded-xl border-purple-200"
                    min="1"
                    max="99"
                    data-testid="discount-percent-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-600">%</span>
                </div>
              </div>
              {discountCode && discountPercent && price && (
                <div className="text-sm text-purple-700 bg-purple-100 p-2 rounded-lg">
                  <p>Vista previa del descuento:</p>
                  <p className="font-semibold">
                    Precio original: ${parseFloat(price).toLocaleString()} → 
                    Precio con cupón: ${(parseFloat(price) * (1 - parseFloat(discountPercent) / 100)).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Auction End Date */}
          {saleType === "auction" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Fecha de finalización</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start rounded-xl border-[#E2E8F0] text-left font-normal"
                      data-testid="date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {auctionEndDate ? (
                        format(auctionEndDate, "PPP", { locale: es })
                      ) : (
                        <span className="text-[#64748B]">Seleccionar fecha</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={auctionEndDate}
                      onSelect={setAuctionEndDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Hora de finalización</Label>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" size={18} />
                  <Input
                    id="time"
                    type="time"
                    value={auctionEndTime}
                    onChange={(e) => setAuctionEndTime(e.target.value)}
                    className="pl-12 rounded-xl border-[#E2E8F0]"
                    data-testid="time-input"
                  />
                </div>
              </div>

              {/* Quick time presets */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => {
                    setAuctionEndDate(addHours(new Date(), 24));
                    setAuctionEndTime(format(addHours(new Date(), 24), "HH:mm"));
                  }}
                >
                  24 horas
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => {
                    setAuctionEndDate(addDays(new Date(), 3));
                    setAuctionEndTime("12:00");
                  }}
                >
                  3 días
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => {
                    setAuctionEndDate(addDays(new Date(), 7));
                    setAuctionEndTime("12:00");
                  }}
                >
                  7 días
                </Button>
              </div>
            </div>
          )}

          {/* Scheduled Publication Option */}
          <div className="space-y-4 p-4 bg-[#FEF3C7] rounded-xl border border-[#F59E0B]/30">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isScheduled"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                className="w-4 h-4 rounded border-[#F59E0B] text-[#F59E0B] focus:ring-[#F59E0B]"
              />
              <Label htmlFor="isScheduled" className="text-[#92400E] font-medium cursor-pointer">
                Programar publicación (Próximamente)
              </Label>
            </div>
            
            {isScheduled && (
              <div className="space-y-4 pl-7">
                <p className="text-xs text-[#92400E]">
                  El producto aparecerá como "Próximamente" con cuenta regresiva hasta la fecha programada.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-[#92400E]">Fecha de publicación</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal rounded-xl border-[#F59E0B]/30"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#F59E0B]" />
                          {scheduledDate ? (
                            format(scheduledDate, "PPP", { locale: es })
                          ) : (
                            <span>Seleccionar fecha</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={scheduledDate}
                          onSelect={setScheduledDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[#92400E]">Hora</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="rounded-xl border-[#F59E0B]/30"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full rounded-full bg-[#475569] hover:bg-[#334155] h-12"
            disabled={loading}
            data-testid="submit-product"
          >
            {loading ? <div className="spinner" style={{ width: 20, height: 20 }} /> : isScheduled ? "Programar Producto" : "Publicar Producto"}
          </Button>
        </form>
      </main>

      <BottomNav />
    </div>
  );
};

export default CreateProductPage;
